import { useState, useCallback } from 'react'
import type { AxiosError } from 'axios'

import { useToast } from '../hooks/use-toast'
import type { HttpResponse } from '../protocols/http-client.protocol'
import type { ResponseAPI } from '../types/response-api.type'
import { useAuthStore } from '../stores/auth.store'
import { MESSAGES } from '../constants/messages'

interface HttpState {
  data: HttpResponse | null
  error: AxiosError<ResponseAPI> | null
  isLoading: boolean
}

interface UseHttpClientOptions {
  onSuccess?: (data: HttpResponse) => void
  onError?: (error: AxiosError<ResponseAPI>) => void
  showToast?: boolean
	showSucessToast?: boolean
}

export function useHttpClient<TPayload = unknown>(
  serviceFunction: (payload: TPayload) => Promise<HttpResponse>,
  options: UseHttpClientOptions = {}
) {
  const { toast } = useToast()
  const { onSuccess, onError, showToast = true, showSucessToast = true } = options

  const [state, setState] = useState<HttpState>({
    data: null,
    error: null,
    isLoading: false,
  })

  const execute = useCallback(async (payload: TPayload) => {
    setState(prev => ({ ...prev, isLoading: true, error: null }))

    try {
      const response = await serviceFunction(payload)

      setState({
        data: response,
        error: null,
        isLoading: false,
      })

      if (showToast) {
        handleToastNotification(response)
      }

      onSuccess?.(response)
      return response

    } catch (error) {
      const axiosError = error as AxiosError<ResponseAPI>

      setState({
        data: null,
        error: axiosError,
        isLoading: false,
      })

      if (showToast) {
        handleErrorNotification(axiosError)
      }

      onError?.(axiosError)
      throw axiosError
    }
  }, [serviceFunction, onSuccess, onError, showToast])

  const handleToastNotification = (data: HttpResponse) => {
    const { status, message } = data

    if (!message) return

    switch (status) {
      case 200:

        if (showSucessToast) {
          toast.success({ title: message })
        }
        break
      case 202:
      case 203:
      case 204:
        toast.info({ title: message })
        break
      case 400:
        toast.error({ title: message })
        break
      default:
        if (status >= 400) {
          toast.error({ title: message })
        }
    }
  }

  const handleErrorNotification = (error: AxiosError<ResponseAPI>) => {
    const errorMessage = error?.response?.data?.message || MESSAGES.ERROR_TITLE

    toast.error({
      title: errorMessage,
      description: !error?.response?.data?.message ? MESSAGES.ERROR_DESCRIPTION : undefined,
    })
  }

  const reset = useCallback(() => {
    setState({
      data: null,
      error: null,
      isLoading: false,
    })
  }, [])

  return {
    execute,
    reset,
    ...state,
  }
}